import { useMemo, useState, type FormEvent } from 'react';
import { IconMessage, IconSend, IconUsers } from './icons';
import type { CrmCustomer } from './customerTypes';
import { contactWhatsappLink } from './contactLinks';

type Props = {
  customers: CrmCustomer[];
  onClose: () => void;
};

export function BulkMessageDialog({ customers, onClose }: Props) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [offerText, setOfferText] = useState('');
  const [opportunity, setOpportunity] = useState('');

  const offerBlock = offerText.trim() ? `Offre : ${offerText.trim()}` : '';
  const oppBlock = opportunity.trim() ? `Opportunité : ${opportunity.trim()}` : '';
  const fullBody = [offerBlock, oppBlock, body].filter(Boolean).join('\n\n');
  const waText = subject ? `${subject}\n\n${fullBody}` : fullBody;

  /** Tous les clients de la base — aucune sélection manuelle. */
  const emails = useMemo(
    () => customers.map((c) => c.email.trim()).filter(Boolean),
    [customers],
  );
  const withPhone = useMemo(
    () =>
      customers
        .filter((c) => c.phone.trim())
        .map((c) => ({
          id: c.id,
          name: `${c.first_name} ${c.last_name}`.trim() || 'Client',
          phone: c.phone.trim(),
        })),
    [customers],
  );
  const withoutEmail = customers.length - emails.length;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (emails.length === 0) return;
    const mailto = `mailto:?bcc=${encodeURIComponent(emails.join(','))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;
    window.location.href = mailto;
    onClose();
  }

  return (
    <div className="crm-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="crm-modal crm-modal--wide"
        role="dialog"
        aria-labelledby="crm-bulk-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="crm-modal-head">
          <h3 id="crm-bulk-title">
            <IconSend size={18} /> Message groupé
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Fermer
          </button>
        </div>

        <div className="crm-bulk-audience" role="status">
          <span className="crm-bulk-audience-icon" aria-hidden>
            <IconUsers size={18} />
          </span>
          <div>
            <p className="crm-bulk-audience-title">
              Envoi à tous les clients ({customers.length})
            </p>
            <p className="crm-bulk-audience-desc">
              Le système sélectionne automatiquement toute votre base clients.
              {emails.length > 0
                ? ` ${emails.length} e-mail${emails.length > 1 ? 's' : ''} en copie cachée (BCC).`
                : ' Aucun e-mail disponible.'}
              {withoutEmail > 0 ? ` ${withoutEmail} sans e-mail ignoré(s).` : ''}
              {withPhone.length > 0
                ? ` ${withPhone.length} contact${withPhone.length > 1 ? 's' : ''} WhatsApp.`
                : ''}
            </p>
          </div>
        </div>

        <form onSubmit={submit}>
          <div className="field">
            <label htmlFor="bulk-offer">Offre / promotion (optionnel)</label>
            <input
              id="bulk-offer"
              value={offerText}
              onChange={(e) => setOfferText(e.target.value)}
              placeholder="Ex. : -15 % sur toute la gamme jusqu’au 30"
            />
          </div>

          <div className="field">
            <label htmlFor="bulk-opportunity">Opportunité (optionnel)</label>
            <input
              id="bulk-opportunity"
              value={opportunity}
              onChange={(e) => setOpportunity(e.target.value)}
              placeholder="Ex. : nouvelle opportunité / contexte commercial"
            />
          </div>

          <div className="field">
            <label htmlFor="bulk-subject">Objet</label>
            <input
              id="bulk-subject"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="bulk-body">Message</label>
            <textarea
              id="bulk-body"
              required
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          {withPhone.length > 0 ? (
            <div className="crm-bulk-wa">
              <p className="crm-msg-label">
                WhatsApp — mêmes destinataires (ouvrir un fil par client)
              </p>
              <ul className="crm-bulk-wa-list">
                {withPhone.map((c) => {
                  const href = contactWhatsappLink(c.phone, waText);
                  return (
                    <li key={c.id}>
                      <span className="crm-bulk-wa-name">{c.name}</span>
                      <span className="crm-bulk-wa-phone">{c.phone}</span>
                      <a
                        href={href || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost crm-bulk-wa-btn"
                        onClick={(e) => {
                          if (!href) e.preventDefault();
                        }}
                      >
                        <IconMessage size={14} /> WhatsApp
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div className="crm-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Annuler
            </button>
            <button className="btn btn-primary" type="submit" disabled={emails.length === 0}>
              Envoyer à tous ({emails.length})
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
